/**
 * Ventes — Cache / React-State mit KV (business_*_data) abgleichen.
 * Phase 1: sales[] lebt im merged payload; Sales-Filter können veralten.
 */

import { syncSalesSnapshot } from '../services/cacheReadModel';
import { getCachedSalesRead, readSalesFromMergedBlob } from './salesReadSource';

export function parseSalesFromBusinessPayload(businessId) {
  if (!businessId) return [];
  const cached = getCachedSalesRead(businessId);
  if (cached) return cached;
  return readSalesFromMergedBlob(businessId);
}

function saleEntityKey(sale) {
  const k = sale?.id || sale?.orderId;
  return k != null && k !== '' ? String(k) : '';
}

function pickRicherSale(existing, incoming) {
  if (!existing) return incoming;
  if (!incoming) return existing;
  const existingDone = existing.status === 'completed';
  const incomingDone = incoming.status === 'completed';
  if (incomingDone && !existingDone) return { ...existing, ...incoming };
  if (existingDone && !incomingDone) return { ...incoming, ...existing };
  const existingName = (existing.customerName || '').trim().length;
  const incomingName = (incoming.customerName || '').trim().length;
  const richer = incomingName > existingName ? incoming : existing;
  const other = richer === incoming ? existing : incoming;
  return { ...other, ...richer };
}

/** Union nach id/orderId — verhindert Lost Sales bei verzögertem Reload. */
export function mergeSalesById(prev = [], loaded = []) {
  const map = new Map();
  for (const sale of [...(Array.isArray(loaded) ? loaded : []), ...(Array.isArray(prev) ? prev : [])]) {
    const key = saleEntityKey(sale);
    if (!key) continue;
    map.set(key, pickRicherSale(map.get(key), sale));
  }
  return Array.from(map.values());
}

function customerEntityKey(customer) {
  const k = customer?.customerId || customer?.id;
  return k != null && k !== '' ? String(k) : '';
}

export function mergeCustomersById(prev = [], loaded = []) {
  const map = new Map();
  for (const customer of [
    ...(Array.isArray(loaded) ? loaded : []),
    ...(Array.isArray(prev) ? prev : [])
  ]) {
    const key = customerEntityKey(customer);
    if (!key) continue;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, customer);
      continue;
    }
    map.set(key, { ...existing, ...customer });
  }
  return Array.from(map.values());
}

export function filterValidCustomers(customers = []) {
  return (Array.isArray(customers) ? customers : []).filter(
    (c) => c && (c.customerId || c.id) && typeof c === 'object'
  );
}

/**
 * Lädt sales[] aus KV und aktualisiert den Read-Model-Cache.
 * @param {string} businessId
 * @param {Array|null} [salesList]
 * @param {{ mode?: 'full'|'silent' }} [options]
 *   - full (default): sales analytics + worker dashboard (heavy)
 *   - silent: snapshot only — use on POS checkout critical path
 */
export function syncSalesCacheFromStorage(businessId, salesList = null, options = {}) {
  const sales = Array.isArray(salesList) ? salesList : parseSalesFromBusinessPayload(businessId);
  syncSalesSnapshot(sales, options);
  return sales;
}

/** Async: Aggregat-Index + Blob, dann Cache + Read Model. */
export async function syncSalesCacheFromStorageAsync(businessId, options = {}) {
  const { refreshSalesReadCache } = await import('./salesReadSource');
  const sales = await refreshSalesReadCache(businessId);
  return syncSalesCacheFromStorage(businessId, sales, options);
}
